import { ReadonlyVec3, vec3 } from 'gl-matrix';

import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    roiPreviewFragmentShader,
    roiPreviewVertexShader,
} from '~/engine/core/render/renderers/glsl';
import { OrbitView, orbitToEyeBasis } from '~/engine/util/geometry/camera';

const FIELD_OF_VIEW_Y_DEG = 35;
/**
 * Hard upper bound on the fragment shader's fixed-step march loop. Duplicates
 * `#define MARCH_STEP_LIMIT` in roi-preview.frag (GLSL needs its own
 * compile-time constant) — keep the two in sync when tuning. The CPU step
 * budget below is derived from it so a large full-resolution ROI is fully
 * traversed within the shader's budget.
 */
const SHADER_MARCH_STEP_LIMIT = 512;
/** Fixed march step as a fraction of the smallest voxel (for a crisp surface). */
const STEP_VOXEL_FRACTION = 0.5;
/**
 * Steps held in reserve below the shader's hard march-loop bound, absorbing the
 * ray's box-entry offset and rounding so the march can never exceed the limit.
 */
const MARCH_STEP_SAFETY_MARGIN = 32;
/**
 * Upper bound on CPU-computed march steps, held below the shader's march-loop
 * bound so a large full-resolution ROI is traversed without exceeding the
 * shader's compile-time limit.
 */
const MAX_MARCH_STEPS = SHADER_MARCH_STEP_LIMIT - MARCH_STEP_SAFETY_MARGIN;
/** Camera framing distance as a multiple of the box's largest extent. */
const FRAME_DISTANCE_FACTOR = 1.9;
/** Opacity of a ghosted volume where another surface is in front of it. */
const OCCLUDED_GHOST_ALPHA = 0.3;

/**
 * One ROI's SDF sub-volume, ready to upload.
 *
 * `originMm` places the volume in the SHARED scene frame that every volume in a
 * single {@link RoiPreviewRenderer.setVolumes} call is expressed in — without
 * it, two ROIs would both be drawn centred on the origin, stacked on each
 * other. All ROIs of a structure set share the image grid's orientation and
 * spacing, so that frame is the grid frame and every box is axis-aligned in it.
 */
export interface PreviewVolume {
    /**
     * One `Uint8Array` per Z slice, each `sizePx[0] * sizePx[1]` voxels.
     * Readonly because the caller hands over the engine's live SDF cache: this
     * renderer reads it to upload and must never write through it.
     */
    pixels: readonly Uint8Array[];
    /** Voxel dimensions [x, y, z], whole voxels. */
    sizePx: readonly [number, number, number];
    /** Voxel spacing per axis, mm. */
    spacingMm: ReadonlyVec3;
    /** Minimum corner of the sub-volume in the shared scene frame, mm. */
    originMm: ReadonlyVec3;
    /** Surface colour, normalised to [0, 1]. */
    color: ReadonlyVec3;
    /** Draw translucently where another volume is nearer, instead of hidden. */
    ghostWhenOccluded?: boolean;
}

/**
 * A contour texture as a volume in its grid's frame, sharing the texture's
 * live SDF cache. The texture must hold an SDF.
 */
export function previewVolumeOf(
    texture: ContourTexture,
    color: ReadonlyVec3
): PreviewVolume {
    const grid = texture.textureGrid;
    const sizePx = texture.sizePx;
    return {
        pixels: texture.pixels,
        // Rounded once here: the renderer's contract is whole voxel counts, and the grid carries them as floats.
        sizePx: [
            Math.round(sizePx[0]),
            Math.round(sizePx[1]),
            Math.round(sizePx[2]),
        ],
        spacingMm: [grid.resX, grid.resY, grid.resZ],
        // Grid-space origins put every volume of one structure set in a shared axis-aligned frame.
        originMm: grid.rotateToGrid(grid.toWorldSpace([0, 0, 0])),
        color,
    };
}

/** A volume uploaded to the GPU, with the geometry its pass needs. */
interface LoadedVolume {
    texture: WebGLTexture;
    /** Voxel dimensions of the allocated texture storage. */
    dims: vec3;
    /** Box corners, translated so the whole scene is centred on the origin. */
    boxMinMm: vec3;
    boxMaxMm: vec3;
    spacingMm: vec3;
    color: vec3;
    /** Fixed march step for this volume, mm. */
    stepMm: number;
    /** See {@link PreviewVolume.ghostWhenOccluded}. */
    ghostWhenOccluded: boolean;
}

/**
 * Isolated WebGL2 ray-marcher that renders a low-resolution 3D preview of one
 * or more ROIs' signed-distance fields (SDFs).
 *
 * Design notes:
 * - Uses its OWN WebGL2 context (not the engine's shared context) so this
 *   read-only, non-clinical aid can never destabilise the clinical render path.
 * - Renders in a SHARED scene frame in mm: each ROI's SDF sub-volume is an
 *   axis-aligned box placed at its own position, and the whole set is
 *   translated so its union is centred on the origin. Working in mm means
 *   anisotropic voxel spacing is reproduced faithfully — a round structure
 *   stays round — and placing each box means several ROIs keep their true
 *   relative positions instead of stacking on each other.
 * - Each ROI is one pass of the same fullscreen ray-march. The shader writes
 *   `gl_FragDepth` from the hit distance and misses `discard`, so the depth
 *   test resolves which surface is nearest along every ray.
 * - The iso surface is the SDF threshold 127.5/255 = 0.5; normals come from the
 *   SDF gradient, which yields smooth shading even from the 8-bit field.
 *
 * Mirrors the recipe validated in the Phase-0 ray-march spike.
 */
export class RoiPreviewRenderer {
    private static readonly BACKGROUND = new Float32Array([0.063, 0.07, 0.086]);

    private readonly canvas: HTMLCanvasElement;
    private readonly gl: WebGL2RenderingContext;
    private readonly program: WebGLProgram;
    private readonly vao: WebGLVertexArrayObject;
    private readonly vbo: WebGLBuffer;
    private readonly uniforms: Record<string, WebGLUniformLocation | null>;

    private volumes: LoadedVolume[] = [];
    /** Half the scene's diagonal, mm; sets the depth range each render. */
    private sceneRadiusMm = 1;
    // Reused across refreshes (live edits reload up to 4 Hz) to avoid
    // reallocating a multi-MB staging buffer per update. Sized to the largest
    // volume seen so far; each upload takes a subarray of exactly its length.
    private packedVoxels: Uint8Array | null = null;

    /** Suggested initial eye distance (mm) to frame the loaded volumes. */
    public frameDistanceMm = 4;

    public constructor(canvas: HTMLCanvasElement) {
        const gl = canvas.getContext('webgl2', {
            antialias: false,
            premultipliedAlpha: false,
        });
        if (!gl) {
            throw new Error(
                'RoiPreviewRenderer: WebGL2 is not available in this browser'
            );
        }
        this.canvas = canvas;
        this.gl = gl;
        this.program = this.buildProgram();
        const triangle = this.buildFullscreenTriangle();
        this.vao = triangle.vao;
        this.vbo = triangle.vbo;
        this.uniforms = this.locateUniforms();
    }

    /**
     * Upload the ROIs to ray-march, replacing whatever was loaded before.
     *
     * Every volume's `originMm` must be expressed in the same frame; the set is
     * re-centred on its own union here, so the caller supplies positions in any
     * consistent frame (the image grid's) without having to centre them.
     *
     * Passing an empty list clears the panel.
     */
    public setVolumes(volumes: readonly PreviewVolume[]): void {
        if (volumes.length === 0) {
            this.releaseVolumes();
            return;
        }

        // Validated in full before anything is uploaded, so a bad volume leaves
        // the loaded scene untouched rather than half-replaced.
        for (const volume of volumes) {
            this.validate(volume);
        }
        const accepted = volumes;

        // Union of every box, so the scene can be centred and the camera framed
        // on all of them together.
        const unionMin = vec3.fromValues(Infinity, Infinity, Infinity);
        const unionMax = vec3.fromValues(-Infinity, -Infinity, -Infinity);
        for (const volume of accepted) {
            const max = vec3.create();
            for (let axis = 0; axis < 3; axis++) {
                max[axis] =
                    volume.originMm[axis] +
                    Math.round(volume.sizePx[axis]) * volume.spacingMm[axis];
            }
            vec3.min(unionMin, unionMin, volume.originMm);
            vec3.max(unionMax, unionMax, max);
        }
        const centre = vec3.scale(
            vec3.create(),
            vec3.add(vec3.create(), unionMin, unionMax),
            0.5
        );
        const extent = vec3.sub(vec3.create(), unionMax, unionMin);

        // Grow the shared staging buffer to the largest volume once, rather
        // than reallocating per volume.
        const largest = Math.max(
            ...accepted.map(
                (volume) =>
                    volume.sizePx[0] * volume.sizePx[1] * volume.sizePx[2]
            )
        );
        if ((this.packedVoxels?.length ?? 0) < largest) {
            this.packedVoxels = new Uint8Array(largest);
        }

        // Textures are reused slot by slot: a live edit reloads the same ROIs
        // at the same extents, which then costs a texSubImage3D and no
        // allocation. Surplus slots from a larger previous selection are freed.
        this.releaseVolumes(accepted.length);
        const next: LoadedVolume[] = [];
        try {
            for (let index = 0; index < accepted.length; index++) {
                next.push(this.uploadVolume(accepted[index], index, centre));
            }
        } catch (error) {
            // Everything is dropped rather than kept: the volumes uploaded
            // before the failure are centred on the new scene and those still
            // held are centred on the old one, so keeping the mixture would
            // draw structures at positions relative to nothing. An empty panel
            // is wrong in an obvious way; a mispositioned one is not.
            //
            // Release `this.volumes`, NOT `next`: at this point `this.volumes`
            // holds every texture still reachable — the slots reused from the
            // previous scene, plus the ones uploadVolume created for this
            // scene up to and including the slot that threw (it records each
            // in `this.volumes` before any call that can fail). `next` holds
            // only the slots that finished, so releasing it would leak the
            // failing slot and every retained slot after it — and, at 4 Hz
            // during a live edit, leak them again on every refresh.
            this.releaseVolumes();
            this.sceneRadiusMm = 1;
            this.frameDistanceMm = 4;
            throw error;
        }

        this.volumes = next;
        this.frameDistanceMm =
            Math.max(extent[0], extent[1], extent[2]) * FRAME_DISTANCE_FACTOR;
        this.sceneRadiusMm = vec3.length(extent) / 2;
    }

    /** Drop the loaded volumes so the next render shows an empty panel. */
    public clearVolume(): void {
        this.releaseVolumes();
    }

    /** Whether any volume is currently loaded and ready to render. */
    public get hasVolumeLoaded(): boolean {
        return this.volumes.length > 0;
    }

    /** How many volumes are currently loaded. */
    public get volumeCount(): number {
        return this.volumes.length;
    }

    private validate(volume: PreviewVolume): void {
        const [nx, ny, nz] = volume.sizePx;
        if (
            !Number.isInteger(nx) ||
            !Number.isInteger(ny) ||
            !Number.isInteger(nz) ||
            nx <= 0 ||
            ny <= 0 ||
            nz <= 0
        ) {
            // Refused rather than skipped: a caller that asks for a structure
            // and is silently given nothing has no way to tell.
            throw new Error(
                `RoiPreviewRenderer.setVolumes: sizePx must be whole ` +
                    `positive voxel counts, got [${nx}, ${ny}, ${nz}]`
            );
        }
        const sliceSize = nx * ny;
        if (volume.pixels.length !== nz) {
            throw new Error(
                `RoiPreviewRenderer.setVolumes: expected ${nz} slices, got ` +
                    `${volume.pixels.length}`
            );
        }
        for (let z = 0; z < nz; z++) {
            if (volume.pixels[z].length !== sliceSize) {
                throw new Error(
                    `RoiPreviewRenderer.setVolumes: slice ${z} has ` +
                        `${volume.pixels[z].length} voxels, expected ` +
                        `${sliceSize}`
                );
            }
        }
        // Spacing and origin decide the box the ray-march runs in. A zero or
        // non-finite value collapses the box, which makes the shader's
        // texture coordinates 0/0 and its march step 0 — a structure that is
        // silently absent from a panel claiming to show the selection, which
        // is the same failure the texture-allocation check exists to prevent.
        for (let axis = 0; axis < 3; axis++) {
            if (
                !Number.isFinite(volume.spacingMm[axis]) ||
                volume.spacingMm[axis] <= 0
            ) {
                throw new Error(
                    `RoiPreviewRenderer.setVolumes: spacingMm must be ` +
                        `finite and positive, got [${volume.spacingMm[0]}, ` +
                        `${volume.spacingMm[1]}, ${volume.spacingMm[2]}]`
                );
            }
            if (!Number.isFinite(volume.originMm[axis])) {
                throw new Error(
                    `RoiPreviewRenderer.setVolumes: originMm must be finite, ` +
                        `got [${volume.originMm[0]}, ${volume.originMm[1]}, ` +
                        `${volume.originMm[2]}]`
                );
            }
        }
    }

    /**
     * Pack, upload and place one volume, reusing slot `index`'s texture when
     * there is one. Returns the entry rather than storing it, so the caller can
     * swap a whole scene in at once or discard a half-built one.
     */
    private uploadVolume(
        volume: PreviewVolume,
        index: number,
        sceneCentreMm: ReadonlyVec3
    ): LoadedVolume {
        const gl = this.gl;
        const [nx, ny, nz] = volume.sizePx;
        const sliceSize = nx * ny;
        const voxelCount = sliceSize * nz;

        // The staging buffer is sized to the largest volume, so take a view of
        // exactly this volume's length: texImage3D reads the whole view.
        const packed = this.packedVoxels!.subarray(0, voxelCount);
        for (let z = 0; z < nz; z++) {
            packed.set(volume.pixels[z], z * sliceSize);
        }

        const existing = this.volumes[index];
        const loaded: LoadedVolume = existing ?? {
            texture: this.createVolumeTexture(),
            dims: vec3.fromValues(0, 0, 0),
            boxMinMm: vec3.create(),
            boxMaxMm: vec3.create(),
            spacingMm: vec3.create(),
            color: vec3.create(),
            stepMm: STEP_VOXEL_FRACTION,
            ghostWhenOccluded: false,
        };
        // Held from here on, so a throw below still leaves the texture
        // reachable for release by the caller's failure path.
        this.volumes[index] = loaded;

        gl.bindTexture(gl.TEXTURE_3D, loaded.texture);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        if (
            nx === loaded.dims[0] &&
            ny === loaded.dims[1] &&
            nz === loaded.dims[2]
        ) {
            // Same extents as the loaded texture: overwrite its storage in
            // place instead of re-specifying (and re-allocating) it.
            gl.texSubImage3D(
                gl.TEXTURE_3D,
                0,
                0,
                0,
                0,
                nx,
                ny,
                nz,
                gl.RED,
                gl.UNSIGNED_BYTE,
                packed
            );
        } else {
            // Storage allocation is the one GL call here that can fail on real
            // data (a structure larger than MAX_3D_TEXTURE_SIZE, or out of
            // video memory). It reports through gl.getError rather than
            // throwing, and recording dims for storage that was never allocated
            // would send every later refresh down the texSubImage3D path
            // against an empty texture — an ROI silently missing from a panel
            // that claims to show the selection.
            gl.getError();
            gl.texImage3D(
                gl.TEXTURE_3D,
                0,
                gl.R8,
                nx,
                ny,
                nz,
                0,
                gl.RED,
                gl.UNSIGNED_BYTE,
                packed
            );
            const error = gl.getError();
            if (error !== gl.NO_ERROR) {
                throw new Error(
                    `RoiPreviewRenderer: allocating a ${nx}x${ny}x${nz} ` +
                        `volume texture failed (GL error ${error})`
                );
            }
            vec3.set(loaded.dims, nx, ny, nz);
        }

        vec3.copy(loaded.spacingMm, volume.spacingMm);
        vec3.copy(loaded.color, volume.color);
        loaded.ghostWhenOccluded = volume.ghostWhenOccluded === true;
        vec3.sub(loaded.boxMinMm, volume.originMm, sceneCentreMm);
        vec3.set(
            loaded.boxMaxMm,
            loaded.boxMinMm[0] + nx * volume.spacingMm[0],
            loaded.boxMinMm[1] + ny * volume.spacingMm[1],
            loaded.boxMinMm[2] + nz * volume.spacingMm[2]
        );

        // Guarantee the fixed-step march crosses this box within the shader's
        // step budget for a large ROI; otherwise use a half-voxel step.
        const diagonalMm = Math.hypot(
            nx * volume.spacingMm[0],
            ny * volume.spacingMm[1],
            nz * volume.spacingMm[2]
        );
        loaded.stepMm = Math.max(
            Math.min(
                volume.spacingMm[0],
                volume.spacingMm[1],
                volume.spacingMm[2]
            ) * STEP_VOXEL_FRACTION,
            diagonalMm / MAX_MARCH_STEPS
        );
        return loaded;
    }

    /**
     * Delete the textures held from index `keepCount` on. Every texture this
     * class creates is released either here or in {@link dispose}.
     */
    private releaseVolumes(keepCount = 0): void {
        for (let index = keepCount; index < this.volumes.length; index++) {
            this.gl.deleteTexture(this.volumes[index].texture);
        }
        if (keepCount < this.volumes.length) {
            this.volumes.length = keepCount;
        }
    }

    public render(view: OrbitView): void {
        const gl = this.gl;
        const width = gl.drawingBufferWidth;
        const height = gl.drawingBufferHeight;
        gl.viewport(0, 0, width, height);
        gl.clearColor(
            RoiPreviewRenderer.BACKGROUND[0],
            RoiPreviewRenderer.BACKGROUND[1],
            RoiPreviewRenderer.BACKGROUND[2],
            1
        );
        gl.clearDepth(1);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        if (this.volumes.length === 0 || width === 0 || height === 0) {
            return;
        }

        const basis = orbitToEyeBasis(view);
        const fovY = (FIELD_OF_VIEW_Y_DEG * Math.PI) / 180;
        const u = this.uniforms;
        gl.useProgram(this.program);
        gl.bindVertexArray(this.vao);
        gl.activeTexture(gl.TEXTURE0);
        gl.uniform1i(u.uSdf, 0);
        gl.uniform3fv(u.uEye, basis.eye);
        gl.uniform3fv(u.uRight, basis.right);
        gl.uniform3fv(u.uUp, basis.up);
        gl.uniform3fv(u.uFwd, basis.forward);
        gl.uniform1f(u.uTanHalf, Math.tan(fovY / 2));
        gl.uniform1f(u.uAspect, width / height);

        // Depth range around the scene, recomputed per frame because zooming
        // moves the eye. Widened by a whole radius each side so a surface can
        // never fall outside it and clamp onto another ROI's depth.
        const eyeDistanceMm = vec3.length(basis.eye);
        const near = Math.max(0, eyeDistanceMm - 2 * this.sceneRadiusMm);
        const far = Math.max(
            near + Number.EPSILON,
            eyeDistanceMm + 2 * this.sceneRadiusMm
        );
        gl.uniform1f(u.uDepthNear, near);
        gl.uniform1f(u.uDepthFar, far);

        // One pass per ROI. Misses discard and hits write gl_FragDepth, so the
        // depth test alone decides which surface is seen where they overlap.
        const march = (volume: LoadedVolume): void => {
            gl.bindTexture(gl.TEXTURE_3D, volume.texture);
            gl.uniform1f(u.uStep, volume.stepMm);
            gl.uniform3fv(u.uBoxMin, volume.boxMinMm);
            gl.uniform3fv(u.uBoxMax, volume.boxMaxMm);
            gl.uniform3fv(u.uSpacing, volume.spacingMm);
            gl.uniform3fv(u.uColor, volume.color);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
        };

        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LESS);
        gl.uniform1f(u.uAlpha, 1);
        const ghosted = this.volumes.filter(
            (volume) => volume.ghostWhenOccluded
        );
        for (const volume of this.volumes) {
            if (!volume.ghostWhenOccluded) {
                march(volume);
            }
        }

        // Translucent pass first, writing no depth, so it and the solid pass
        // partition the ghosted pixels and each is painted exactly once.
        if (ghosted.length > 0) {
            gl.depthFunc(gl.GREATER);
            gl.depthMask(false);
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.uniform1f(u.uAlpha, OCCLUDED_GHOST_ALPHA);
            for (const volume of ghosted) {
                march(volume);
            }

            // Back to the state every other pass here assumes.
            gl.disable(gl.BLEND);
            gl.depthMask(true);
            gl.depthFunc(gl.LESS);
            gl.uniform1f(u.uAlpha, 1);
            for (const volume of ghosted) {
                march(volume);
            }
        }
        gl.disable(gl.DEPTH_TEST);
        gl.bindVertexArray(null);
    }

    /**
     * Releases what this renderer allocated, and the context itself once the
     * canvas has left the document. A canvas still on the page keeps its
     * context live: getContext hands that same context to the next renderer
     * made on it (React re-runs a mount effect on the same canvas), and a
     * lost one would leave that renderer drawing nothing.
     */
    public dispose(): void {
        const gl = this.gl;
        this.releaseVolumes();
        gl.deleteVertexArray(this.vao);
        gl.deleteBuffer(this.vbo);
        gl.deleteProgram(this.program);
        if (!this.canvas.isConnected) {
            gl.getExtension('WEBGL_lose_context')?.loseContext();
        }
    }

    private buildProgram(): WebGLProgram {
        const gl = this.gl;
        const program = gl.createProgram();
        if (!program) {
            throw new Error('RoiPreviewRenderer: failed to create GL program');
        }
        try {
            for (const [type, source] of [
                [gl.VERTEX_SHADER, roiPreviewVertexShader],
                [gl.FRAGMENT_SHADER, roiPreviewFragmentShader],
            ] as const) {
                const shader = this.compileShader(type, source);
                gl.attachShader(program, shader);
                // The program holds what it needs of it, so the shader object
                // goes with the program rather than living as long as the
                // context.
                gl.deleteShader(shader);
            }
            gl.linkProgram(program);
            if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
                const log = gl.getProgramInfoLog(program);
                throw new Error(
                    `RoiPreviewRenderer: program link failed: ${log}`
                );
            }
        } catch (error) {
            // Whatever failed, the program and the shaders attached to it are
            // released rather than left on a context this renderer abandons.
            gl.deleteProgram(program);
            throw error;
        }
        return program;
    }

    private compileShader(type: number, source: string): WebGLShader {
        const gl = this.gl;
        const shader = gl.createShader(type);
        if (!shader) {
            throw new Error('RoiPreviewRenderer: failed to create shader');
        }
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const log = gl.getShaderInfoLog(shader);
            gl.deleteShader(shader);
            throw new Error(
                `RoiPreviewRenderer: shader compile failed: ${log}`
            );
        }
        return shader;
    }

    private buildFullscreenTriangle(): {
        vao: WebGLVertexArrayObject;
        vbo: WebGLBuffer;
    } {
        const gl = this.gl;
        const vao = gl.createVertexArray();
        if (!vao) {
            throw new Error('RoiPreviewRenderer: failed to create VAO');
        }
        gl.bindVertexArray(vao);
        const vbo = gl.createBuffer();
        if (!vbo) {
            throw new Error('RoiPreviewRenderer: failed to create VBO');
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array([-1, -1, 3, -1, -1, 3]),
            gl.STATIC_DRAW
        );
        const location = gl.getAttribLocation(this.program, 'aPos');
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
        gl.bindVertexArray(null);
        return { vao, vbo };
    }

    private createVolumeTexture(): WebGLTexture {
        const gl = this.gl;
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error('RoiPreviewRenderer: failed to create 3D texture');
        }
        gl.bindTexture(gl.TEXTURE_3D, texture);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        return texture;
    }

    private locateUniforms(): Record<string, WebGLUniformLocation | null> {
        const gl = this.gl;
        const names = [
            'uSdf',
            'uEye',
            'uRight',
            'uUp',
            'uFwd',
            'uTanHalf',
            'uAspect',
            'uStep',
            'uBoxMin',
            'uBoxMax',
            'uSpacing',
            'uColor',
            'uAlpha',
            'uDepthNear',
            'uDepthFar',
        ];
        const map: Record<string, WebGLUniformLocation | null> = {};
        for (const name of names) {
            map[name] = gl.getUniformLocation(this.program, name);
        }
        return map;
    }
}
